/* Empty states, callouts and field wrappers — the three places where the
 * two admin apps currently improvise.
 *
 * EmptyState replaces "no hay nada" centred in a <tbody> (console Jobs)
 * and the bare paragraphs the builder uses. It is the portal's own
 * pattern — icon in a circle, title, one sentence, one call to action —
 * which is the reference this whole effort is aiming at.
 *
 * Callout is where long guidance goes when it will not fit in a
 * subtitle. It is CAPPED: three lines, then a "more" that hands off to
 * the help rail. The cap exists because the console's Marca tab opens
 * with a nine-line amber block before the first field, and the builder's
 * Integraciones page documents a credential in more pixels than the
 * credential itself occupies.
 *
 * FormField is only for controls @omnitok/ui does not already label —
 * a colour picker, a code editor, a chips input. For a plain input or
 * select, use the kit's own ``label`` / ``helperText`` / ``error`` props;
 * two labelling mechanisms on one control is how you get two of them on
 * screen.
 */

import { AlertTriangle, Info, XCircle } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cx } from "./layout";

export interface EmptyStateProps {
  /** A lucide icon, already sized. */
  icon?: ReactNode;
  title: string;
  description?: string;
  /** One call to action. Two is a menu, not an empty state. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): ReactNode {
  return (
    <div className={cx("px-6 py-12 text-center", className)}>
      {icon !== undefined && (
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary">
          {icon}
        </div>
      )}
      <p className="font-display text-[15px] font-extrabold text-neutral-900">{title}</p>
      {description !== undefined && (
        <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-neutral-500">
          {description}
        </p>
      )}
      {action !== undefined && <div className="mt-5">{action}</div>}
    </div>
  );
}

export type CalloutTone = "info" | "warning" | "danger";

// Look v3 (2026-10-24), the same callout as the reports': a quiet tinted
// surface behind ONE uniform border; the tone lives in the icon and the
// title (shape and word, never the surface alone).
const CALLOUT: Record<CalloutTone, { surface: string; tone: string; icon: ReactNode }> = {
  info: {
    surface: "bg-primary-soft/50",
    tone: "text-primary-ink",
    icon: <Info size={15} />,
  },
  warning: {
    surface: "bg-warning-soft/40",
    tone: "text-warning-dark",
    icon: <AlertTriangle size={15} />,
  },
  danger: {
    surface: "bg-error-soft/40",
    tone: "text-error-dark",
    icon: <XCircle size={15} />,
  },
};

export interface CalloutProps {
  tone?: CalloutTone;
  title?: string;
  children: ReactNode;
  /** Renders a disclosure toggle when the body is long. The caller
   *  supplies both labels because this package carries no copy. */
  moreLabel?: string;
  lessLabel?: string;
  /** Extra body revealed by the toggle. */
  more?: ReactNode;
  className?: string;
}

export function Callout({
  tone = "info",
  title,
  children,
  moreLabel,
  lessLabel,
  more,
  className,
}: CalloutProps): ReactNode {
  const [open, setOpen] = useState(false);
  const spec = CALLOUT[tone];
  const canExpand = more !== undefined && moreLabel !== undefined;
  return (
    <div
      className={cx(
        "flex gap-3 rounded-[11px] border border-neutral-200 px-5 py-4 text-xs leading-[1.75] text-neutral-600",
        spec.surface,
        className,
      )}
    >
      <span
        className={cx(
          "grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg border border-primary-line bg-white",
          spec.tone,
        )}
        aria-hidden="true"
      >
        {spec.icon}
      </span>
      <div className="min-w-0">
        {title !== undefined && (
          <p className={cx("mb-0.5 font-display text-sm font-extrabold", spec.tone)}>{title}</p>
        )}
        <div>{children}</div>
        {canExpand && (
          <>
            {open && <div className="mt-2 leading-relaxed">{more}</div>}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-2 text-2xs font-semibold text-primary underline underline-offset-2"
            >
              {open ? (lessLabel ?? moreLabel) : moreLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export interface FormFieldProps {
  label: string;
  /** Sits under the control. 12 px, neutral-500 (look v3). */
  help?: string;
  /** Replaces ``help`` and is the only red in the field. */
  error?: string;
  /** Marks the control as required for screen readers and sighted users. */
  required?: boolean;
  /** ``id`` of the control, so the label actually points at it. */
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

export function FormField({
  label,
  help,
  error,
  required = false,
  htmlFor,
  children,
  className,
}: FormFieldProps): ReactNode {
  return (
    <div className={cx("min-w-0", className)}>
      <label
        htmlFor={htmlFor}
        className="mb-1 block text-xs font-semibold text-neutral-700"
      >
        {label}
        {required && (
          <span className="ml-0.5 text-error-dark" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {error !== undefined ? (
        <p className="mt-1 text-xs text-error-dark">{error}</p>
      ) : help !== undefined ? (
        <p className="mt-1 text-xs text-neutral-500">{help}</p>
      ) : null}
    </div>
  );
}
